import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Paper,
  Skeleton,
  useTheme,
  useMediaQuery,
  Alert,

} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VerifiedIcon from '@mui/icons-material/Verified';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

import { color, radius } from '../../theme/tokens';
import { searchProductsForSupplement } from './amazonApi';
import { logAnalyticsEvent } from '../../utils/analytics';
import {
  findProductBySlug,
  getUnitText,
  formatSupplementName,
  generateProductDescription,
  getProductCertifications,
  getDietaryAttributes,
} from '../../utils/productUtils';
import ProductCard from './ProductCard';
import { styles } from './ProductDetail.styles';
import { ShoppingCartIcon, CheckCircleIcon, ScienceIcon } from '../../icons';

const encodePathSegment = (value) => encodeURIComponent(String(value || '').trim());

const ProductDetail = () => {
  const { supplementKey, productSlug } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Scroll to top when product changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [productSlug]);

  useEffect(() => {
    const loadProduct = async () => {
      if (!supplementKey || !productSlug) {
        setError('Invalid product URL');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const products = await searchProductsForSupplement(supplementKey);

        if (!products || products.length === 0) {
          setError('No products found for this supplement');
          setLoading(false);
          return;
        }

        const foundProduct = findProductBySlug(products, productSlug);

        if (!foundProduct) {
          setError('Product not found');
          setLoading(false);
          return;
        }

        setProduct(foundProduct);

        const related = products
          .filter(p => p.asin !== foundProduct.asin)
          .slice(0, 4);
        setRelatedProducts(related);

        logAnalyticsEvent('view_item', {
          item_id: foundProduct.asin,
          item_name: foundProduct.product_name,
          item_brand: foundProduct.brand,
          item_category: 'product',
          item_variant: supplementKey,
        });

      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error loading product:', err);
        }
        setError('Failed to load product. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [supplementKey, productSlug]);

  const supplementName = useMemo(() => formatSupplementName(supplementKey), [supplementKey]);
  const certifications = useMemo(() => getProductCertifications(product), [product]);
  const dietaryAttributes = useMemo(() => getDietaryAttributes(product), [product]);
  const metaDescription = useMemo(() => generateProductDescription(product), [product]);
  const encodedSupplementKey = encodePathSegment(supplementKey);
  const canonicalUrl = `https://my-stack.ai/market/${encodedSupplementKey}/${encodePathSegment(productSlug)}`;

  // SEO structured data
  const structuredData = useMemo(() => {
    if (!product) return null;

    const price = product.price_per_bottle || product.current_price || product.price;
    const affiliateUrl = product.affiliate_link || (product.asin ? `https://www.amazon.com/dp/${product.asin}` : null);

    return {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": product.product_name,
      "description": metaDescription,
      "image": product.image_url,
      "brand": {
        "@type": "Brand",
        "name": product.brand
      },
      "sku": product.asin,
      "category": "Health & Household > Vitamins & Supplements",
      "additionalProperty": [
        product.form_factor && {
          "@type": "PropertyValue",
          "name": "Form Factor",
          "value": product.form_factor
        },
        product.serving_size && {
          "@type": "PropertyValue",
          "name": "Serving Size",
          "value": `${product.serving_size} ${getUnitText(product.form_factor, product.serving_size)}`
        },
        product.servings_per_bottle && {
          "@type": "PropertyValue",
          "name": "Servings Per Container",
          "value": product.servings_per_bottle
        },
      ].filter(Boolean),
      ...(affiliateUrl && {
        "offers": {
          "@type": "Offer",
          "url": affiliateUrl,
          "priceCurrency": "USD",
          ...(price && !isNaN(parseFloat(price)) && {
            "price": parseFloat(price).toFixed(2),
          }),
          "availability": "https://schema.org/InStock",
          "seller": {
            "@type": "Organization",
            "name": "Amazon"
          }
        },
      }),
      ...(product.rating && product.review_count > 0 && {
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": product.rating,
          "reviewCount": product.review_count,
          "bestRating": "5",
          "worstRating": "1"
        }
      })
    };
  }, [product, metaDescription]);

  const breadcrumbData = useMemo(() => {
    if (!product) return null;

    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Market", "item": "https://my-stack.ai/market" },
        { "@type": "ListItem", "position": 2, "name": supplementName, "item": `https://my-stack.ai/market/${encodedSupplementKey}` },
        { "@type": "ListItem", "position": 3, "name": product.product_name, "item": canonicalUrl }
      ]
    };
  }, [product, supplementName, encodedSupplementKey, canonicalUrl]);

  const handleBuyClick = () => {
    logAnalyticsEvent('click_buy_product', {
      item_id: product?.asin,
      item_name: product?.product_name,
      item_brand: product?.brand,
      item_category: 'product',
      item_list_context: 'product_detail_page',
    });
  };

  if (loading) {
    return (
      <Box sx={styles.pageWrapper}>
        <Container maxWidth="lg" sx={{ py: 3 }}>
          <Skeleton variant="text" width={220} height={20} sx={{ mb: 2.5 }} />
          <Paper sx={{ borderRadius: radius.lg, overflow: 'hidden' }}>
            <Grid container>
              <Grid item xs={12} md={5}>
                <Skeleton variant="rectangular" height={350} />
              </Grid>
              <Grid item xs={12} md={7} sx={{ p: 4 }}>
                <Skeleton variant="text" width={80} height={16} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="85%" height={36} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="60%" height={36} sx={{ mb: 3 }} />
                <Skeleton variant="rectangular" width={180} height={48} sx={{ borderRadius: 2 }} />
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={styles.pageWrapper}>
        <Container maxWidth="lg" sx={{ py: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/market/${supplementKey || ''}`)}
            sx={{ textTransform: 'none' }}
          >
            Back to {supplementName || 'Market'}
          </Button>
        </Container>
      </Box>
    );
  }

  if (!product) return null;

  // Build product specifications
  const specifications = [
    product.form_factor && { label: 'Form Factor', value: product.form_factor },
    product.specific_nutrient_form && { label: 'Nutrient Form', value: product.specific_nutrient_form },
    product.serving_size && { label: 'Serving Size', value: `${product.serving_size} ${getUnitText(product.form_factor, product.serving_size)}` },
    product.dosage_amount && { label: 'Amount Per Serving', value: `${product.dosage_amount} ${product.dosage_unit || ''}`.trim() },
    product.servings_per_bottle && { label: 'Servings Per Container', value: product.servings_per_bottle },
  ].filter(Boolean);

  // Build quality/certification badges
  const qualityItems = [
    product.third_party_tested && { label: 'Third-Party Tested', verified: true },
    product.gmp_certified_facility && { label: 'GMP Certified', verified: true },
    ...certifications.map(cert => ({ label: cert, verified: true })),
  ].filter(Boolean);

  return (
    <>
      <Helmet>
        <title>{product.product_name} | {product.brand} | myStack Market</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={`${product.product_name} | ${product.brand}`} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={product.image_url} />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${product.product_name} | ${product.brand}`} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={product.image_url} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbData)}</script>
      </Helmet>

      <Box sx={styles.pageWrapper}>
        <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3 } }}>
          {/* Breadcrumbs */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2.5 }}>
            <Box
              component={RouterLink}
              to="/market"
              sx={{ fontSize: '0.85rem', color: color.evergreen, textDecoration: 'none', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
            >
              Market
            </Box>
            <NavigateNextIcon sx={{ fontSize: '1rem', color: color.inkMuted }} />
            <Box
              component={RouterLink}
              to={`/market/${supplementKey}`}
              sx={{ fontSize: '0.85rem', color: color.evergreen, textDecoration: 'none', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
            >
              {supplementName}
            </Box>
            <NavigateNextIcon sx={{ fontSize: '1rem', color: color.inkMuted }} />
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: color.inkSoft }}>
              {product.brand}
            </Typography>
          </Box>

          {/* Hero Card */}
          <Paper elevation={0} sx={styles.heroCard}>
            <Grid container>
              {/* Product Image */}
              <Grid item xs={12} md={5}>
                <Box sx={styles.imageContainer}>
                  <Box
                    component="img"
                    src={product.image_url}
                    alt={product.product_name}
                    sx={styles.productImage}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                    }}
                  />
                </Box>
              </Grid>

              {/* Product Info */}
              <Grid item xs={12} md={7}>
                <Box sx={styles.productInfo}>
                  <Typography sx={styles.brand}>
                    {product.brand}
                  </Typography>

                  <Typography variant="h1" sx={styles.productTitle}>
                    {product.product_name}
                  </Typography>

                  {/* Buy Button */}
                  <Button
                    variant="contained"
                    startIcon={<ShoppingCartIcon sx={{ fontSize: '1.1rem' }} />}
                    href={product.affiliate_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleBuyClick}
                    sx={styles.buyButton}
                  >
                    Buy on Amazon
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Content Grid */}
          <Grid container spacing={3} sx={{ mt: 0 }}>
            {/* Left Column: Specifications */}
            {specifications.length > 0 && (
              <Grid item xs={12} md={6}>
                <Box sx={styles.section}>
                  <Typography sx={styles.sectionTitle}>
                    Product Specifications
                  </Typography>
                  <Box>
                    {specifications.map((spec, index) => (
                      <Box key={index} sx={styles.specRow}>
                        <Typography sx={styles.specLabel}>{spec.label}</Typography>
                        <Typography sx={styles.specValue}>{spec.value}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Grid>
            )}

            {/* Right Column: Quality & Dietary */}
            <Grid item xs={12} md={6}>
              {/* Quality & Certifications */}
              {qualityItems.length > 0 && (
                <Box sx={styles.section}>
                  <Typography sx={styles.sectionTitle}>
                    Quality & Certifications
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                    {qualityItems.slice(0, 6).map((item, index) => (
                      <Box key={index} sx={styles.qualityBadge}>
                        <VerifiedIcon sx={styles.qualityIcon} />
                        <Typography sx={styles.qualityText}>{item.label}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Dietary Information */}
              {dietaryAttributes.length > 0 && (
                <Box sx={{ ...styles.section, mt: 3 }}>
                  <Typography sx={styles.sectionTitle}>
                    Dietary Information
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                    {dietaryAttributes.map((attr) => (
                      <Box key={attr.key} sx={styles.dietaryChip}>
                        <CheckCircleIcon sx={styles.dietaryIcon} />
                        <Typography sx={styles.dietaryText}>{attr.label}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Grid>
          </Grid>

          {/* Product Features (from PA API bullet points) */}
          {Array.isArray(product.features) && product.features.length > 0 && (
            <Box sx={{ ...styles.section, mt: 3 }}>
              <Typography sx={styles.sectionTitle}>
                Product Features
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 0, listStyle: 'none' }}>
                {product.features.map((feature, i) => (
                  <Box
                    key={i}
                    component="li"
                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}
                  >
                    <FiberManualRecordIcon sx={{ fontSize: '0.5rem', mt: '0.45rem', color: color.evergreen, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.9rem', color: color.ink, lineHeight: 1.6 }}>
                      {feature}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Research link — keyword-rich internal link to the supplement page */}
          {supplementKey && (
            <Box
              component={RouterLink}
              to={`/supplements/${supplementKey}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mt: 3,
                p: 2,
                borderRadius: radius.sm,
                border: `0.0625rem solid ${color.lineSoft}`,
                backgroundColor: color.surface,
                textDecoration: 'none',
                '&:hover': { backgroundColor: color.mint, borderColor: color.accent },
                transition: 'background-color 0.15s, border-color 0.15s',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <ScienceIcon sx={{ color: color.evergreen, fontSize: '1.4rem', flexShrink: 0 }} />
                <Box>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: color.evergreen }}>
                    Learn more about {supplementName}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: color.inkSoft, mt: 0.25 }}>
                    Clinical research and dosage information
                  </Typography>
                </Box>
              </Box>
              <NavigateNextIcon sx={{ color: color.evergreen, flexShrink: 0 }} />
            </Box>
          )}

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <Box sx={styles.relatedSection}>
              <Typography sx={styles.relatedHeader}>
                More {supplementName} Products
              </Typography>
              <Grid container spacing={2}>
                {relatedProducts.map((relatedProduct) => (
                  <Grid item xs={6} sm={6} md={3} key={relatedProduct.asin}>
                    <ProductCard
                      product={relatedProduct}
                      compact
                      linkToProductPage={true}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

                  </Container>
      </Box>
    </>
  );
};

export default ProductDetail;
